/**
 * Settings Service
 * API calls for settings management
 */

import api from './api.js';
import type { ApiResponse } from '@/types/api.js';
import type {
  TenantSettings,
  SettingsUpdateInput,
  TaxRate,
  UserProfile,
  UserProfileUpdateInput,
} from '@/types/settings.js';

export const settingsService = {
  // Tenant Settings

  /**
   * Get tenant settings
   */
  async getSettings(): Promise<TenantSettings> {
    const response = await api.get<ApiResponse<TenantSettings>>('/settings');
    return response.data.data;
  },

  /**
   * Update tenant settings
   */
  async updateSettings(data: SettingsUpdateInput): Promise<TenantSettings> {
    const response = await api.put<ApiResponse<TenantSettings>>('/settings', data);
    return response.data.data;
  },

  /**
   * Upload tenant logo
   */
  async uploadLogo(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post<ApiResponse<{ url: string }>>('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  // Tax Rates

  /**
   * Get tax rates
   */
  async getTaxRates(): Promise<TaxRate[]> {
    const response = await api.get<ApiResponse<TaxRate[]>>('/settings/tax-rates');
    return response.data.data;
  },

  /**
   * Create tax rate
   */
  async createTaxRate(data: { name: string; rate: number; isDefault?: boolean }): Promise<TaxRate> {
    const response = await api.post<ApiResponse<TaxRate>>('/settings/tax-rates', data);
    return response.data.data;
  },

  /**
   * Update tax rate
   */
  async updateTaxRate(
    id: string,
    data: Partial<{ name: string; rate: number; isDefault: boolean; isActive: boolean }>
  ): Promise<TaxRate> {
    const response = await api.put<ApiResponse<TaxRate>>(`/settings/tax-rates/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete tax rate
   */
  async deleteTaxRate(id: string): Promise<void> {
    await api.delete(`/settings/tax-rates/${id}`);
  },

  // User Profile

  /**
   * Get user profile
   */
  async getProfile(): Promise<UserProfile> {
    const response = await api.get<ApiResponse<UserProfile>>('/settings/profile');
    return response.data.data;
  },

  /**
   * Update user profile
   */
  async updateProfile(data: UserProfileUpdateInput): Promise<UserProfile> {
    const response = await api.put<ApiResponse<UserProfile>>('/settings/profile', data);
    return response.data.data;
  },

  /**
   * Upload user avatar
   */
  async uploadAvatar(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post<ApiResponse<{ url: string }>>('/settings/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /**
   * Change password
   */
  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    await api.post('/settings/profile/change-password', data);
  },

  /**
   * Enable MFA
   */
  async enableMfa(): Promise<{ secret: string; qrCode: string }> {
    const response = await api.post<ApiResponse<{ secret: string; qrCode: string }>>(
      '/settings/profile/mfa/enable'
    );
    return response.data.data;
  },

  /**
   * Verify MFA
   */
  async verifyMfa(code: string): Promise<void> {
    await api.post('/settings/profile/mfa/verify', { code });
  },

  /**
   * Disable MFA
   */
  async disableMfa(code: string): Promise<void> {
    await api.post('/settings/profile/mfa/disable', { code });
  },
};

export default settingsService;
